import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, ApiRequestError, type Assignment, type BoardState, type ShiftPost } from '@pinchcrew/core'
import { t } from '../i18n'

export type ShiftFilter = 'all' | 'tonight' | 'weekend'

export const useShiftsStore = defineStore('shifts', () => {
  const available = ref<ShiftPost[]>([])
  const upcoming = ref<Assignment[]>([])
  const past = ref<Assignment[]>([])
  const loading = ref(false)
  const claimingId = ref<string | null>(null)
  const error = ref<string | null>(null)
  const filter = ref<ShiftFilter>('all')

  const filtered = computed(() => {
    if (filter.value === 'all') return available.value
    const now = new Date()

    if (filter.value === 'tonight') {
      const endOfToday = new Date(now)
      endOfToday.setHours(23, 59, 59, 999)
      return available.value.filter(s => new Date(s.starts_at) <= endOfToday)
    }

    // weekend = the coming Saturday and Sunday
    return available.value.filter(s => {
      const day = new Date(s.starts_at).getDay()
      return day === 0 || day === 6
    })
  })

  /** A shift the worker is clocked into right now, if any. */
  const activeShift = computed(() =>
    upcoming.value.find(a => a.attendance?.clock_in && !a.attendance.clock_out) ?? null,
  )

  const board = ref<BoardState | null>(null)

  async function loadAvailable() {
    loading.value = true
    error.value = null
    try {
      const page = await api.browseShifts()
      available.value = page.results
      // Why the board is empty, when it is — so the UI can say something
      // true instead of "no shifts right now".
      board.value = page.board ?? null
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
    } finally {
      loading.value = false
    }
  }

  async function loadMine() {
    const [up, pastList] = await Promise.all([
      api.getMyAssignments('upcoming'), api.getMyAssignments('past'),
    ])
    upcoming.value = up
    past.value = pastList
  }

  /**
   * Claim a shift. Returns the assignment on success, or null with `error`
   * set — the caller shows the message rather than throwing at the user.
   */
  async function claim(shiftId: string): Promise<Assignment | null> {
    claimingId.value = shiftId
    error.value = null
    try {
      const assignment = await api.claimShift(shiftId)
      available.value = available.value.filter(s => s.id !== shiftId)
      upcoming.value = [...upcoming.value, assignment]
        .sort((a, b) => a.shift_post.starts_at.localeCompare(b.shift_post.starts_at))
      return assignment
    } catch (e) {
      if (e instanceof ApiRequestError) {
        // Prefer a localised message for known codes.
        const key = `shifts.errors.${e.code}`
        const localised = t(key)
        error.value = localised === key ? e.message : localised
        // A full shift is stale data — refresh so it disappears.
        if (e.code === 'shift_full') await loadAvailable()
      } else {
        error.value = t('common.somethingWrong')
      }
      return null
    } finally {
      claimingId.value = null
    }
  }

  async function cancel(assignmentId: string) {
    error.value = null
    try {
      await api.cancelClaim(assignmentId)
      await Promise.all([loadMine(), loadAvailable()])
      return true
    } catch (e) {
      error.value = e instanceof ApiRequestError ? e.message : t('common.somethingWrong')
      return false
    }
  }

  function clearError() { error.value = null }

  return {
    available, upcoming, past, loading, claimingId, error, filter, board,
    filtered, activeShift,
    loadAvailable, loadMine, claim, cancel, clearError,
  }
})
